import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { useSeo } from '@/hooks/useSeo';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { hotTopics, siteInfo } from '@/mocks/site';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug = '' } = useParams();
  const article = articles.find((item) => item.slug === slug);

  const category = categories.find((c) => c.slug === article?.categorySlug);
  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return article.related
      .map((s) => articles.find((a) => a.slug === s))
      .filter((a): a is (typeof articles)[number] => Boolean(a))
      .slice(0, 3);
  }, [article]);

  const sameCategory = useMemo(() => {
    if (!article) return [];
    return articles.filter((a) => a.categorySlug === article.categorySlug && a.slug !== article.slug).slice(0, 4);
  }, [article]);

  const outline = useMemo(
    () =>
      (article?.content ?? [])
        .map((block, index) => ({ id: `section-${index}`, heading: block.heading }))
        .filter((item): item is { id: string; heading: string } => Boolean(item.heading)),
    [article],
  );

  const jsonLd = useMemo(() => {
    if (!article) return undefined;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const basePath = typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__.replace(/\/$/, '') : '';
    const siteUrl = `${origin}${basePath}`;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline: article.title,
        description: article.summary,
        datePublished: article.publishedISO,
        dateModified: article.publishedISO,
        inLanguage: 'zh-CN',
        articleSection: category?.name,
        keywords: article.tags.join(','),
        author: { '@type': 'Organization', name: article.author },
        publisher: {
          '@type': 'Organization',
          name: siteInfo.name,
          url: `${siteUrl}/`,
        },
        mainEntityOfPage: `${siteUrl}/article/${article.slug}`,
        url: `${siteUrl}/article/${article.slug}`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${siteUrl}/` },
          {
            '@type': 'ListItem',
            position: 2,
            name: category?.name ?? '主题分类',
            item: `${siteUrl}/category/${article.categorySlug}`,
          },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
    ];
  }, [article, category]);

  useSeo({
    title: article ? `${article.title} | 17c吃瓜` : '事件详情 - 17c吃瓜',
    description: article ? article.summary : siteInfo.description,
    keywords: article ? [...article.tags, '17c吃瓜', '今日热门事件'] : ['17c吃瓜'],
    path: `/article/${slug}`,
    ogType: 'article',
    jsonLd,
  });

  if (!article || !category) {
    return <NotFound />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full">
        <div className="w-full px-4 md:px-8 pt-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '主题分类', to: '/#topics' },
              { label: category.name, to: `/category/${category.slug}` },
              { label: article.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-8 py-6 md:py-8">
          <header className="max-w-4xl">
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <Link
                to={`/category/${category.slug}`}
                className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-800 border border-primary-200 hover:bg-primary-200 transition-colors cursor-pointer"
              >
                {category.name}
              </Link>
              <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 border border-secondary-200">
                <i className="ri-fire-fill mr-0.5 text-primary-500"></i>热度 {article.heat}
              </span>
              <span className="text-foreground-500">更新于 {article.publishedAt}</span>
            </div>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl lg:text-5xl font-black text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed">
              {article.subtitle}
            </p>
            <div className="mt-5 pt-5 border-t border-background-200 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {article.views} 阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                约 {article.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line"></i>
                已更新 {article.timeline.length} 个节点
              </span>
            </div>
          </header>

          <div className="mt-7 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 lg:gap-10">
            <div className="min-w-0">
              <div className="w-full h-56 md:h-96 rounded-lg overflow-hidden border border-background-200 bg-background-100">
                <img
                  src={article.cover}
                  alt={`${article.title} · 17c吃瓜事件配图`}
                  title={`${article.title} · 今日热门事件`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <section className="mt-7 rounded-lg border border-accent-200 bg-accent-50 p-5">
                <h2 className="font-heading text-base font-bold text-accent-900">
                  <i className="ri-list-check-2 mr-1.5"></i>
                  关键要点速览
                </h2>
                <ul className="mt-3 space-y-2.5">
                  {article.keyPoints.map((point) => (
                    <li key={point} className="flex items-start gap-2.5 text-sm text-accent-900 leading-relaxed">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-accent-500 shrink-0"></span>
                      {point}
                    </li>
                  ))}
                </ul>
              </section>

              <section className="mt-7">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  事件时间线
                </h2>
                <ol className="relative border-l-2 border-background-200 pl-5 space-y-5">
                  {article.timeline.map((item) => (
                    <li key={`${item.time}-${item.text}`} className="relative">
                      <span className="absolute -left-[27px] top-1 w-3.5 h-3.5 rounded-full bg-primary-500 border-2 border-background-50"></span>
                      <p className="text-xs text-primary-700">{item.time}</p>
                      <p className="mt-1 text-sm text-foreground-700 leading-relaxed">{item.text}</p>
                    </li>
                  ))}
                </ol>
              </section>

              <div className="mt-8 space-y-6">
                {article.content.map((block, index) => (
                  <section key={`block-${index}`} id={`section-${index}`} className="scroll-mt-32">
                    {block.heading && (
                      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                        {block.heading}
                      </h2>
                    )}
                    {block.paragraphs.map((paragraph) => (
                      <p key={paragraph} className="text-sm md:text-base text-foreground-700 leading-loose mb-4">
                        {paragraph}
                      </p>
                    ))}
                  </section>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-background-200">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-foreground-500">标签</span>
                    {article.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/category/${article.categorySlug}`}
                        className="px-2.5 py-1 text-xs rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        #{tag}
                      </Link>
                    ))}
                  </div>
                </div>
                <p className="mt-4 text-xs text-foreground-500 leading-relaxed">
                  本文内容整理自各方公开发布的信息，仅供参考阅读。若你发现表述有误，欢迎通过「
                  {siteInfo.email}」与我们联系。
                </p>
              </div>
            </div>

            <aside className="flex flex-col gap-5">
              {outline.length > 0 && (
                <nav className="rounded-lg border border-background-200 bg-background-100 p-5" aria-label="内容目录">
                  <h2 className="font-heading text-sm font-bold text-foreground-950">
                    <i className="ri-file-list-3-line mr-1.5 text-primary-600"></i>
                    内容目录
                  </h2>
                  <ul className="mt-3 space-y-2">
                    {outline.map((item) => (
                      <li key={item.id}>
                        <a
                          href={`#${item.id}`}
                          className="block text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                        >
                          {item.heading}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">
                  <i className="ri-fire-line mr-1.5 text-primary-600"></i>
                  热榜速览
                </h2>
                <ol className="mt-2">
                  {hotTopics.slice(0, 5).map((item) => (
                    <li key={item.slug}>
                      <Link
                        to={`/article/${item.slug}`}
                        className="group flex items-start gap-2.5 py-2.5 border-b border-background-100 last:border-b-0 cursor-pointer"
                      >
                        <span
                          className={`w-5 h-5 shrink-0 flex items-center justify-center rounded text-[10px] font-bold ${
                            item.rank <= 3
                              ? 'bg-primary-500 text-background-50'
                              : 'bg-background-200 text-foreground-600'
                          }`}
                        >
                          {item.rank}
                        </span>
                        <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors leading-snug">
                          {item.title}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
                <h2 className="font-heading text-sm font-bold text-primary-900">订阅热点更新</h2>
                <p className="mt-2 text-xs text-primary-800 leading-relaxed">
                  每天固定时段把当日讨论量最高的事件整理成一封邮件，三分钟读完。
                </p>
                <Link
                  to="/#subscribe"
                  className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-send-line"></i>
                  前往订阅
                </Link>
              </div>
            </aside>
          </div>
        </article>

        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="w-full px-4 md:px-8">
            <h2 className="font-heading text-lg md:text-2xl font-bold text-foreground-950 mb-5">
              相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {(relatedArticles.length > 0 ? relatedArticles : sameCategory).map((item) => (
                <ArticleCard key={item.slug} article={item} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}